import React, { useState } from 'react';
import { Card, Row, Col, Typography, Collapse, Form, Input, Button, message, Tag, Space, Divider } from 'antd';
import { QuestionCircleOutlined, MailOutlined, PhoneOutlined, MessageOutlined, SendOutlined, SafetyCertificateOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { contactService } from '../services/api';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const faqData = [
  { q: 'How do I place an order?', a: 'Browse our products, add items to your cart, and proceed to checkout. You can pay with eSewa, Khalti, or Cash on Delivery.' },
  { q: 'How do I track my order?', a: 'Go to Orders in your dashboard and click "Track" on any active order. You can see real-time delivery status and location.' },
  { q: 'What is your return policy?', a: 'We offer a 7-day return policy on most products. Items must be unused and in original packaging. Contact support to initiate a return.' },
  { q: 'How do I use my loyalty points?', a: 'Go to the Rewards page to view your points balance. You can redeem points for wallet credit when you have enough.' },
  { q: 'How does the referral program work?', a: 'Share your unique referral code with friends. When they sign up and place their first order, you earn a bonus.' },
  { q: 'What payment methods do you accept?', a: 'We accept eSewa, Khalti, digital wallet, and Cash on Delivery (COD).' },
  { q: 'How can I change my delivery address?', a: 'Go to Addresses in your profile to add, edit, or set a default delivery address.' },
  { q: 'How do I cancel an order?', a: 'You can cancel an order from the Orders page before it is processed. Once confirmed, contact support for cancellations.' },
];

const HelpSupport = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = { name: values.name, email: values.email, subject: values.subject, message: values.message, department: 'support' };
      const res = await contactService.submit(payload);
      message.success('Message sent! Our support team will get back to you within 24 hours.');
      form.resetFields();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to send message. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>Help & Support</Title>
        <Paragraph>We're here to help. Browse FAQs or send us a message.</Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={8}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card className="customer-card" style={{ textAlign: 'center' }}>
              <MessageOutlined style={{ fontSize: 32, color: '#52b788', marginBottom: 8 }} />
              <Title level={5}>Live Chat</Title>
              <Text type="secondary">Available 24/7</Text>
              <Button type="link" style={{ display: 'block', marginTop: 8 }}>Start Chat</Button>
            </Card>
            <Card className="customer-card" style={{ textAlign: 'center' }}>
              <MailOutlined style={{ fontSize: 32, color: '#2e9bd6', marginBottom: 8 }} />
              <Title level={5}>Email Us</Title>
              <Text>support@currentcare.com</Text>
              <div><Text type="secondary" style={{ fontSize: 12 }}>Response within 2 hours</Text></div>
            </Card>
            <Card className="customer-card" style={{ textAlign: 'center' }}>
              <PhoneOutlined style={{ fontSize: 32, color: '#2d5a3d', marginBottom: 8 }} />
              <Title level={5}>Call Us</Title>
              <Text>+977-1-2345678</Text>
              <div><Text type="secondary" style={{ fontSize: 12 }}>Mon-Fri, 9AM-6PM</Text></div>
            </Card>
          </Space>
        </Col>

        <Col xs={24} md={16}>
          <Card className="customer-card" title={<span><QuestionCircleOutlined /> Frequently Asked Questions</span>} style={{ marginBottom: 24 }}>
            <Collapse accordion items={faqData.map((faq, i) => ({ key: i, label: faq.q, children: <Text>{faq.a}</Text> }))} />
          </Card>

          <Card className="customer-card" title={<span><SendOutlined /> Send Us a Message</span>}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="name" label="Your Name" rules={[{ required: true }]}>
                    <Input style={{ borderRadius: 10 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                    <Input style={{ borderRadius: 10 }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
                <Input style={{ borderRadius: 10 }} />
              </Form.Item>
              <Form.Item name="message" label="Message" rules={[{ required: true, min: 10 }]}>
                <TextArea rows={4} style={{ borderRadius: 10 }} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} icon={<SendOutlined />} className="customer-btn-primary">
                Send Message
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default HelpSupport;
